<form
  data-testid="claim-form"
  [formGroup]="form"
>
  <div class="claim-header">
    <div class="claim-title">
      <span class="claim-badge">{{ index + 1 }}</span>
      <span class="claim-index">
        {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.TITLE" | translate }}
      </span>
    </div>
    @if (isDeleteable) {
      <button
        payee-mini-icon-button
        color="white"
        (click)="deleteClaim()"
      >
        <payee-icon
          name="trash-red"
          width="16px"
          height="16px"
        ></payee-icon>
      </button>
    }
  </div>

  @if (file.value) {
    <section class="flex vcenter uploaded-file">
      <payee-icon
        name="upload-paper"
        class="file-icon"
      ></payee-icon>
      @if (isFileUploaded) {
        <a
          class="file-name"
          target="_blank"
          data-testid="claim-file-name"
          [href]="getFileStorageUrl()"
        >
          {{ getFileName() }}
        </a>
      }
      @if (!isFileUploaded) {
        <a
          class="file-name"
          href="#"
          role="button"
          data-testid="claim-file-name"
          (click)="openLocalFile(); $event.preventDefault()"
          (keydown.enter)="openLocalFile(); $event.preventDefault()"
        >
          {{ getFileName() }}
        </a>
      }
      @if (isEditable) {
        <button
          type="button"
          payee-mini-icon-button
          color="white"
          class="remove-file-button"
          [attr.aria-label]="'CASE_FORM.FORMS.CLAIMS.CLAIM.REMOVE_FILE' | translate"
          (click)="removeFile()"
        >
          <payee-icon
            name="pencil"
            width="16px"
            height="16px"
          ></payee-icon>
        </button>
      }
    </section>
  }

  @if (!file.value && isEditable) {
    <section class="claim-file-upload">
      <div
        class="claim-dropzone"
        dropzone
        role="button"
        tabindex="0"
        [class.has-error]="file.invalid && file.dirty"
        [class.loading]="ocrLoading()"
        (dropped)="fileDropped($event)"
        (click)="!ocrLoading() && claimFileInput.click()"
        (keydown.enter)="!ocrLoading() && claimFileInput.click()"
        (keydown.space)="!ocrLoading() && claimFileInput.click(); $event.preventDefault()"
      >
        <input
          #claimFileInput
          type="file"
          hidden
          multiple="false"
          accept="application/pdf,
      image/png,
      image/jpeg,
      image/webp"
          data-testid="claim-file-input"
          [disabled]="ocrLoading()"
          (change)="onClaimFileSelected($event)"
        />
        @if (ocrLoading()) {
          <mat-progress-spinner
            mode="indeterminate"
            [diameter]="32"
          ></mat-progress-spinner>
        }
        @if (!ocrLoading()) {
          <mat-icon class="claim-dropzone-icon">upload_file</mat-icon>
          <div class="claim-dropzone-text">
            <div class="claim-dropzone-title">
              {{ "CASE_FORM.FORMS.INVOICE.COMPACT_UPLOAD_TITLE" | translate }}
            </div>
            <div class="claim-dropzone-subtitle">
              {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.UPLOAD_PROMPT" | translate }}
            </div>
          </div>
        }
      </div>
      @if (file.invalid && file.dirty) {
        <span
          class="claim-file-error"
          color="alert-red"
          role="alert"
        >
          {{ "ERROR_MESSAGES.FILE_REQUIRED" | translate }}
        </span>
      }
    </section>
  }

  <div class="amount-section">
    <span class="label">
      {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.AMOUNT_LABEL" | translate }}
    </span>
    <div
      class="amount-combined"
      [class.error]="original_amount.invalid && original_amount.dirty"
    >
      <input
        class="amount-input"
        placeholder="0"
        type="text"
        inputmode="numeric"
        data-testid="claim-amount"
        [value]="amountDisplay()"
        (input)="onAmountInput($event)"
      />
      <div
        class="currency-picker"
        data-testid="claim-currency-picker"
        [matMenuTriggerFor]="currencyMenu"
      >
        <img
          class="currency-flag"
          [src]="currency_iso.value | currencyFlagUrl"
          [alt]="currency_iso.value"
        />
        <span
          class="currency-code"
          data-testid="claim-currency"
        >
          {{ currency_iso.value }}
        </span>
        <mat-icon class="currency-arrow">expand_more</mat-icon>
      </div>
      <mat-menu
        #currencyMenu
        xPosition="before"
      >
        <div class="currency-menu">
          <div class="currency-section-label">
            {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.POPULAR_CURRENCIES" | translate }}
          </div>
          @for (c of topCurrencies; track c) {
            <div
              class="currency-dropdown-item"
              role="button"
              tabindex="0"
              [class.selected]="c === currency_iso.value"
              (click)="selectCurrency(c)"
              (keydown.enter)="selectCurrency(c)"
              (keydown.space)="selectCurrency(c); $event.preventDefault()"
            >
              <img
                class="currency-flag"
                [src]="c | currencyFlagUrl"
                [alt]="c"
              />
              <span>{{ c }}</span>
            </div>
          }
          @if (restCurrencies.length) {
            <div class="currency-section-label">
              {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.ALL_CURRENCIES" | translate }}
            </div>
          }
          @for (c of restCurrencies; track c) {
            <div
              class="currency-dropdown-item"
              role="button"
              tabindex="0"
              [class.selected]="c === currency_iso.value"
              (click)="selectCurrency(c)"
              (keydown.enter)="selectCurrency(c)"
              (keydown.space)="selectCurrency(c); $event.preventDefault()"
            >
              <img
                class="currency-flag"
                [src]="c | currencyFlagUrl"
                [alt]="c"
              />
              <span>{{ c }}</span>
            </div>
          }
        </div>
      </mat-menu>
    </div>
    @if (original_amount.invalid && original_amount.dirty) {
      @if (original_amount.hasError("required")) {
        <span
          color="alert-red"
          role="alert"
        >
          {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
        </span>
      }
      @if (original_amount.hasError("max")) {
        <span
          color="alert-red"
          role="alert"
        >
          {{
            "CASE_FORM.FORMS.CLAIMS.CLAIM.ERRORS.MAX_AMOUNT"
              | translate
                : {
                    max: original_amount.errors?.max?.max | number: "1.0" : "hu",
                    currency: currency_iso.value,
                  }
          }}
        </span>
      }
    }
  </div>

  @if (original_amount.valid && currency_iso.valid) {
    <div class="due-date-section">
      <span class="label">
        {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.DUE_DATE_LABEL" | translate }}
      </span>
      <div
        class="date-combined"
        [class.error]="due_date_at.invalid && due_date_at.dirty"
      >
        <input
          #dateTextInput
          class="date-input"
          type="text"
          formControlName="due_date_at"
          data-testid="claim-due-date"
          [placeholder]="'CASE_FORM.FORMS.CLAIMS.CLAIM.DUE_DATE_PLACEHOLDER' | translate"
          [matDatepicker]="picker"
          [max]="maxDueDate"
          (focus)="picker.open()"
        />
        <button
          type="button"
          class="date-picker-trigger"
          (click)="picker.open()"
        >
          <payee-icon
            name="calendar"
            width="24"
            height="24"
          ></payee-icon>
        </button>
        <mat-datepicker #picker></mat-datepicker>
      </div>
      @if (due_date_at.invalid && due_date_at.dirty) {
        <span
          color="alert-red"
          role="alert"
        >
          @switch (true) {
            @case (due_date_at.hasError("required")) {
              {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
            }
            @case (due_date_at.hasError("maxdate")) {
              {{ "CASE_FORM.FORMS.CLAIMS.CLAIM.ERRORS.DUE_DATE_EXCEEDED" | translate }}
            }
            @default {
              {{ "ERROR_MESSAGES.INVALID_FORMAT" | translate }}
              {{ due_date_at.errors | json }}
            }
          }
        </span>
      }
    </div>
  }
</form>
